import { View } from "@-/com.npmjs.react-native";
import { GlassButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { LearnFlashcardShell } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { useLearnFlashcardStageTopInsets } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { useContext } from "react";

/**
 * The summary's resolver failed. Same frame as the loading fallback, but the card explains the
 * failure and the CTA slot retries — never the fallback's disabled "Start Reviewing", which reads
 * as "still loading" and gave a failed query no end and no way forward.
 */
export function LearnSummaryPageErrorView({
  error,
  isRetrying,
  onRetry,
}: {
  error: Error;
  isRetrying: boolean;
  onRetry: () => void;
}) {
  const bottomInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const { cardTop } = useLearnFlashcardStageTopInsets();
  return (
    <View className="min-h-0 flex-1 bg-muted" testID="learn-summary-error">
      <ScreenReadyMarker screen="learn-summary" />
      <LearnFlashcardShell
        className="mx-2.25 mb-2.25 min-h-0 flex-1 shadow-sm"
        style={{
          marginLeft: isLandscape ? diameter + 18 : undefined,
          marginRight: isLandscape ? diameter + 18 : undefined,
          marginTop: cardTop,
        }}
        testID="learn-summary-card"
      >
        <View className="flex-1 items-center justify-center gap-3 px-6">
          <Text className="text-center font-sans text-[22px] font-semibold" colorVariant="default">
            Couldn&apos;t load your reviews
          </Text>
          <Text className="text-center text-sm text-muted-foreground" selectable>
            {error.message}
          </Text>
        </View>
      </LearnFlashcardShell>
      <View
        className={
          isLandscape
            ? "absolute right-2.25 bottom-2.25 items-center"
            : "mx-2.25 flex-row items-center justify-between gap-2.25"
        }
        style={
          isLandscape
            ? { top: diameter + 18, width: diameter }
            : { height: diameter, marginBottom: 9 + bottomInset }
        }
      >
        <GlassButton
          accessibilityLabel="Try Again"
          className={
            isLandscape
              ? "size-full rounded-full p-0 shadow-xs"
              : "h-full min-w-0 flex-1 rounded-full px-2 py-0 shadow-xs"
          }
          isLoading={isRetrying}
          onPress={onRetry}
          size={isLandscape ? "fill" : undefined}
          testID="learn-summary-error-retry"
        >
          {isLandscape ? (
            <Icon className="text-foreground" name="LuRotateCcw" size={22} />
          ) : (
            <Text className="text-base leading-6" colorVariant="default" numberOfLines={1}>
              Try Again
            </Text>
          )}
        </GlassButton>
      </View>
    </View>
  );
}
